/**
 * A rollup chart over the page's range (ADR 0109): stacked bars per hour or day, by group or by
 * metric, with the chart family's loading, empty, partial, error and live states.
 */
import { HISTORY_PEAK_METRICS } from '@orgus/protocol';
import type { HistoryMetric, HistorySeriesResult } from '@orgus/protocol';
import { OTHER_KEY, StackedBarChart, foldOther, formatCompactNumber, formatDateRange, formatDay, formatHour } from '@orgus/ui';
import type { DateRange, StackKey } from '@orgus/ui';
import type { ReactNode } from 'react';
import { useHistorySeries } from './data.ts';

export const METRIC_LABEL: Record<HistoryMetric, string> = {
  'sessions.started': 'Started',
  'sessions.ended': 'Ended',
  turns: 'Turns',
  toolCalls: 'Tool calls',
  'tokens.input': 'Input tokens',
  'tokens.output': 'Output tokens',
  'tokens.cacheRead': 'Cache reads',
  'cost.usd': 'Spend',
  'machines.onlineMinutes': 'Online minutes',
  'machines.online': 'Online machines',
  missions: 'Missions',
  approvals: 'Approvals',
  landings: 'Landings',
  incidents: 'Incidents',
  audit: 'Audit entries',
  'wire.patches': 'Patches',
  'wire.bytes': 'Wire bytes',
};

const GROUP_LABEL: Record<string, string> = { '': 'All', other: 'Other', none: 'None', done: 'Finished', empty: 'No turns', gone: 'Stopped reporting', 'claude-code': 'Claude Code', codex: 'Codex', gemini: 'Gemini', ok: 'Succeeded', error: 'Failed' };
/** A group's name: known words, an audit action without its family ("mission.start": "Start"), else as recorded (a model, a machine). */
// A machine the history outlived is grouped by its bare id ("m_mvyh2lr..."): name it for a person (R-FN-8).
const REMOVED_MACHINE_RE = /^m_[a-z0-9]{8,}$/;
export const groupLabel = (g: string): string => GROUP_LABEL[g] ?? (REMOVED_MACHINE_RE.test(g) ? 'Removed machine' : null) ?? (/^[a-z]+\.[a-z_.-]+$/.test(g) ? g.replace(/^[a-z]+\./, '').replace(/[-_.]/g, ' ').replace(/^./, (c) => c.toUpperCase()) : g);

/** The x label of a bucket. */
export function bucketLabel(t: number, bucket: 'hour' | 'day'): string {
  return bucket === 'hour' ? formatHour(t) : formatDay(t);
}

/** Bars from a result: one key per group of `metric`, or one per metric when `byMetric`. */
export function toStack(r: HistorySeriesResult, metrics: readonly HistoryMetric[], byMetric: boolean, scale = 1): { keys: StackKey[]; data: { label: string; values: Record<string, number> }[] } {
  const rows = r.series.filter((s) => metrics.includes(s.metric));
  const named = rows.map((s) => ({ key: byMetric ? s.metric : s.group || s.metric, label: byMetric ? METRIC_LABEL[s.metric] : groupLabel(s.group), values: s.values }));
  const totals = named.map((n) => ({ ...n, total: n.values.reduce((a, b) => a + b, 0) }));
  const sorted = totals.map((n) => ({ key: n.key, label: n.label, value: n.total })).sort((a, b) => b.value - a.value);
  const folded = foldOther(sorted, 6, (rest) => ({ value: rest.reduce((a, x) => a + x.value, 0) }));
  const keep = new Set(folded.map((f) => f.key));
  const keys: StackKey[] = folded.map((f) => ({ key: f.key, label: f.label }));
  const data = r.buckets.map((t, i) => {
    const values: Record<string, number> = {};
    for (const n of named) {
      const k = keep.has(n.key) ? n.key : OTHER_KEY;
      values[k] = (values[k] ?? 0) + (n.values[i] ?? 0) * scale;
    }
    return { label: bucketLabel(t, r.bucket), values };
  });
  return { keys, data };
}

/** "History starts Oct 20": when the range reaches back past the first recorded day. */
export function historySince(since: string | null, from: number): string | null {
  if (!since) return null;
  const t = Date.parse(`${since}T00:00:00Z`);
  if (!(t > from + 86_400_000)) return null;
  return `History starts ${formatDay(t)}: earlier days have no record.`;
}

/**
 * The empty state's copy (QA-2). History accrues from the hub's first start, so a range that
 * reaches back before it is not "no work": it is "no record". Then the note says when history
 * starts instead of suggesting a longer range; otherwise the caller's copy, or the default.
 */
export function historyEmpty(noun: string, since: string | null, from: number, hasData: boolean, emptyText?: { title: string; description?: string }): { title: string; description?: string } {
  if (hasData && !since) return { title: 'No history recorded yet', description: 'History starts when this hub first records work: check back once work has run.' };
  const note = hasData ? historySince(since, from) : null;
  if (note) return { title: 'Nothing recorded in this range yet', description: note };
  return emptyText ?? { title: `No ${noun} in this range`, description: 'Pick a longer range, or check back once work has run.' };
}

export interface HistoryChartProps {
  range: DateRange;
  title: ReactNode;
  metrics: readonly HistoryMetric[];
  /** Stack one metric by this dimension; without it, the metrics stack. */
  groupBy?: string;
  format?: (n: number) => string;
  /** Multiply values (minutes to hours: 1/60). */
  scale?: number;
  height?: number;
  /** "sessions started": what the summary counts. */
  noun: string;
  actions?: ReactNode;
  className?: string;
  emptyText?: { title: string; description?: string };
}

export function HistoryChart({ range, title, metrics, groupBy, format = formatCompactNumber, scale = 1, height = 180, noun, actions, className, emptyText }: HistoryChartProps) {
  const r = useHistorySeries(range, { metrics, groupBy: groupBy ?? null });
  const stack = r.data ? toStack(r.data, metrics, !groupBy, scale) : { keys: [], data: [] };
  // A peak metric reads as its highest bucket, the rest as their total.
  const peak = metrics.every((m) => HISTORY_PEAK_METRICS.includes(m));
  const perBucket = stack.data.map((d) => Object.values(d.values).reduce((x, y) => x + y, 0));
  const sum = peak ? Math.max(0, ...perBucket) : perBucket.reduce((a, b) => a + b, 0);
  const label = formatDateRange(range);
  const since = r.data ? historySince(r.data.since, range.from) : null;
  return (
    <StackedBarChart
      className={className}
      title={title}
      summary={`${format(sum)} ${noun}, ${label}`}
      keys={stack.keys}
      data={stack.data}
      format={format}
      height={height}
      loading={r.loading}
      error={r.data ? undefined : (r.error ?? undefined)}
      onRetry={r.retry}
      partial={r.error && r.data ? 'Showing the last answer: the newest did not load.' : since}
      emptyText={historyEmpty(noun, r.data?.since ?? null, range.from, !!r.data, emptyText)}
      actions={
        <span className="flex items-center gap-2">
          {actions}
          <span className="text-caption text-fg-subtle num">{r.live ? `Live, ${format(sum)}` : format(sum)}</span>
        </span>
      }
    />
  );
}
